import { Logger } from '@saviynt/common';

import {
  CERTS_LIST_ALL_STATUS,
  CERTS_LIST_PREVIEW_STATUS,
  CERTS_LIST_STATUS,
  CERTS_LIST_TODO_TAB,
} from '../../../components/CertsList/constant';
import { httpService } from '../../../http-service/httpService';
import { LIMIT_CHARACTERS_FOR_QUERY } from '../../../page/CertificationList/CertsListBody/constant';
/* eslint-disable import/no-cycle */
import { setCertsListActionStatus } from '../action';
import {
  RESET_DYNAMIC_SORT_FILTER_DETAILS,
  SET_APPLIED_ROLE_FILTERS,
  SET_DYNAMIC_SORT_FILTER_DETAILS,
  SET_EXPORT_LOADING,
  SET_ROLE_FILTER,
  SET_SEARCH_TEXT_VALUE,
} from '../types';
import { URL_LIST_DATA_API } from '../urls';

const initialOptions = [
  {
    label: 'Consultant',
    value: 'Consultant',
    isChecked: false,
  },
  {
    label: 'Primary Certifier',
    value: 'Primary Certifier',
    isChecked: false,
  },
  {
    label: 'Secondary Certifier',
    value: 'Secondary Certifier',
    isChecked: false,
  },
  {
    label: 'Delegate',
    value: 'Delegate',
    isChecked: false,
  },
];

const adminOptions = [
  {
    label: 'CERTS_LIST_ACTIVE',
    value: '1',
    isChecked: false,
  },
  {
    label: 'CERTS_LIST_INACTIVE',
    value: '0',
    isChecked: false,
  },
];

/**
 *
 * @param {*} columnName  -  sort with column
 * @param {*} sortDirection - sort direction ASC / DESC
 * @returns {function} A thunk that dispatches an action with type `SET_DYNAMIC_SORT_FILTER_DETAILS` and the `columnName` and `sortDirection` as payload.
 */
export const setDynamicSortDetails =
  (columnName, sortDirection) => async (dispatch) => {
    dispatch({
      type: SET_DYNAMIC_SORT_FILTER_DETAILS,
      payload: { columnName, sortDirection },
    });
  };

/**
 *
 * @returns {function} A thunk that dispatches an action with type `RESET_DYNAMIC_SORT_FILTER_DETAILS` reset sort details
 */
export const resetDynamicSortDetails = () => async (dispatch) => {
  dispatch({ type: RESET_DYNAMIC_SORT_FILTER_DETAILS });
};

/**
 *
 * @param {*} rowFilters - Array of row filters
 * @returns {function} A thunk that dispatches an action with type `SET_ROLE_FILTER` and array of filter details as payload
 */
export const setRoleFilters = (rowFilters) => async (dispatch) => {
  dispatch({ type: SET_ROLE_FILTER, payload: rowFilters });
};

/**
 *
 * @param {*} appliedRoles - Array of Applied row filters
 * @returns  {function} A thunk that dispatches an action with type `SET_APPLIED_ROLE_FILTERS` and applied array of filter details as payload
 */
export const setAppliedRoleFilters = (appliedRoles) => async (dispatch) => {
  dispatch({ type: SET_APPLIED_ROLE_FILTERS, payload: appliedRoles });
};

/**
 *
 * @returns  {function} A thunk that dispatches an action setRoleFilters and initial row filter array
 */
export const getRoleFilters = () => async (dispatch, getState) => {
  const { isAdminLogin } = getState().certifications.list.core;

  if (isAdminLogin) {
    dispatch(setRoleFilters(adminOptions));
  } else {
    dispatch(setRoleFilters(initialOptions));
  }
};

/**
 *
 * @param {*} columnName - Column name to sort the list
 * @param {*} sortDirection -  ASC/DESC direction
 * @returns {function} A thunk that dispatches an action with type `SET_DYNAMIC_SORT_FILTER_DETAILS` to set sort details
 */
export const setSortFilters =
  (columnName, sortDirection) => async (dispatch) => {
    dispatch({
      type: SET_DYNAMIC_SORT_FILTER_DETAILS,
      payload: {
        columnName,
        sortDirection,
      },
    });
  };

/**
 *
 * @returns {function} A thunk that dispatches an action with type `RESET_DYNAMIC_SORT_FILTER_DETAILS` to reset sort details
 */
export const resetSortFilters = () => (dispatch) => {
  dispatch({
    type: RESET_DYNAMIC_SORT_FILTER_DETAILS,
  });
};

export const setSearchText = (searchText) => (dispatch) => {
  dispatch({
    type: SET_SEARCH_TEXT_VALUE,
    payload: searchText,
  });
};

export const exportCertListPage =
  (campaignKey) => async (dispatch, getState) => {
    dispatch({
      type: SET_EXPORT_LOADING,
    });

    try {
      const {
        core: { isAdminLogin, hasAuditorLoggedIn },
        tabs: { activeTab },
        viewControls: {
          searchText,
          dynamicSortFilter: { sortByColumn, sortDirection: sortOrder },
          appliedRoleFilters,
        },
      } = getState().certifications.list;

      const body = {};

      // when search text length is greater than 2
      if (searchText.length > LIMIT_CHARACTERS_FOR_QUERY) {
        body.sSearch = searchText;
      }

      // when sort is applied
      if (sortByColumn && sortOrder) {
        body.sort = sortByColumn;
        body.order = sortOrder;
      }

      // when role filter / Active and Inactive records filter
      if (
        (isAdminLogin || hasAuditorLoggedIn) &&
        appliedRoleFilters?.length > 0
      ) {
        body.certifierStatus = appliedRoleFilters
          .map((option) => option.value)
          .join(',');
      }

      if (activeTab && CERTS_LIST_STATUS[activeTab]) {
        if (
          (isAdminLogin || hasAuditorLoggedIn) &&
          activeTab === CERTS_LIST_TODO_TAB
        ) {
          body.status = `${CERTS_LIST_STATUS[activeTab]},${CERTS_LIST_PREVIEW_STATUS}`;
        } else {
          body.status = CERTS_LIST_STATUS[activeTab];
        }
      } else if (!isAdminLogin || !hasAuditorLoggedIn) {
        // when user is not admin passing all status to api
        body.status = CERTS_LIST_ALL_STATUS;
      }

      const exportUrl = `${URL_LIST_DATA_API}?format=xls&extension=xls`;

      if (campaignKey) {
        body.campaignKey = campaignKey;
      }

      const response = await httpService.post(exportUrl, body, {
        responseType: 'blob', // Make sure the response is treated as a Blob (for file downloads)
      });

      const { data, status } = response;

      if (status === 200) {
        const blob = data;
        const contentDisposition = response.headers['content-disposition']; // Get Content-Disposition header
        const filename = contentDisposition
          ? contentDisposition.split('filename=')[1].replace(/"/g, '') // Extract the filename from header
          : 'certs-list-page-file'; // Default filename if not found

        // Create a URL for the Blob
        const downloadUrl = window.URL.createObjectURL(blob);

        // Create a temporary anchor tag to trigger the download
        const a = document.createElement('a');

        a.href = downloadUrl;
        a.download = filename; // Use the filename from Content-Disposition header
        a.click(); // Programmatically click to trigger the download

        // Clean up the object URL after the download
        window.URL.revokeObjectURL(downloadUrl);
      }

      return true;
    } catch (error) {
      Logger.log('Error: exportCertListPage', error);

      if (typeof error === 'string') {
        dispatch(
          setCertsListActionStatus({
            action: 'DOWNLOAD_EXCEL',
            status: 'FAILED',
            response: error,
          })
        );
      } else {
        dispatch(
          setCertsListActionStatus({
            action: 'DOWNLOAD_EXCEL',
            status: 'FAILED',
            response: 'Export operation failed, please try again later.',
          })
        );
      }

      return false;
    } finally {
      dispatch({
        type: SET_EXPORT_LOADING,
      });
    }
  };
